/* global SideDrawer */

if (!customElements.get('facet-filters')) {
  class FacetFilters extends SideDrawer {
    connectedCallback() {
      this.init();
    }

    disconnectedCallback() {
      window.removeEventListener('popstate', this.historyChangeHandler);
      if (this.breakpointChangeHandler) {
        window.removeEventListener('on:breakpoint-change', this.breakpointChangeHandler);
      }
    }

    init() {
      this.filteringEnabled = this.dataset.filtering === 'true';
      this.sortingEnabled = this.dataset.sorting === 'true';
      this.form = document.getElementById('facets');
      this.results = document.getElementById('filter-results');
      this.expanded = [];
      this.filterChangeTimeout = null;
      this.searchInputValues = {};

      this.handleBreakpointChange();
      this.addElements();
      this.addListeners();
      this.initSearchInputs();
      
      if (window.innerWidth < 1024) {
        this.initMobileEnhancements();
        this.filters.addEventListener('change', (e) => {
          if (e.target.type === 'checkbox') this.updateFilterCountBadge();
        });
      }

      this.historyChangeHandler = this.historyChangeHandler || this.handleHistoryChange.bind(this);
      window.addEventListener('popstate', this.historyChangeHandler);
    }

    addElements() {
      if (this.filteringEnabled) {
        this.filters = this.querySelector('.facets__filters');
        this.activeFilters = this.querySelector('.facets__active-filters');
        this.activeFiltersList = this.querySelector('.active-filters');
        this.activeFiltersHeader = this.querySelector('.active-filters-header');
        this.footer = this.querySelector('.facets__footer');
      }

      if (this.sortingEnabled) {
        this.mobileSortByOptions = this.querySelectorAll('.js-drawer-sort-by');
        this.desktopSortBy = document.querySelector('.products-toolbar__sort');
      }
    }

    addListeners() {
      if (this.filteringEnabled) {
        this.breakpointChangeHandler = this.breakpointChangeHandler || this.handleBreakpointChange.bind(this);
        this.filters.addEventListener('click', this.handleFiltersClick.bind(this));
        this.filters.addEventListener('input', this.handleFilterChange.bind(this));
        this.filters.addEventListener('change', this.handleFilterChange.bind(this));
        this.activeFilters.addEventListener('click', this.handleActiveFiltersClick.bind(this));
        window.addEventListener('on:breakpoint-change', this.breakpointChangeHandler);
      }

      if (this.sortingEnabled) {
        this.desktopSortBy.addEventListener('change', this.handleFilterChange.bind(this));
      }
    }

    initMobileEnhancements() {
      if (window.innerWidth >= 1024) return;
      
      this.initMobileClearAll();
      this.initShowMoreLess();
      this.initOverlay();
      this.initSwipeToClose();
      this.initQuickFilters();
      this.ensureOverlayHidden();
    }

    ensureOverlayHidden() {
      const isDrawerOpen = this.getAttribute('aria-hidden') === 'false' || this.hasAttribute('open');
      if (!isDrawerOpen && this.overlay) {
        this.overlay.classList.remove('is-visible');
      }
    }

    initQuickFilters() {
      const quickFilterBtns = this.querySelectorAll('[data-quick-filter-value]');
      
      quickFilterBtns.forEach((btn) => {
        const filterValues = btn.getAttribute('data-quick-filter-value').split(',').map(v => v.trim());
        const filterParam = btn.getAttribute('data-quick-filter-param') || '';
        
        const matchingCheckboxes = filterValues.map(value => this.findCheckboxByLabelOrParam(filterParam, value)).filter(Boolean);
        const allChecked = matchingCheckboxes.length > 0 && matchingCheckboxes.every(cb => cb.checked);
        if (allChecked) btn.classList.add('is-active');
        
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          
          const checkboxes = filterValues.map(value => this.findCheckboxByLabelOrParam(filterParam, value)).filter(Boolean);
          
          if (checkboxes.length > 0) {
            const allCurrentlyChecked = checkboxes.every(cb => cb.checked);
            const newState = !allCurrentlyChecked;
            
            checkboxes.forEach(checkbox => checkbox.checked = newState);
            btn.classList.toggle('is-active', newState);
            
            const lastCheckbox = checkboxes[checkboxes.length - 1];
            lastCheckbox.dispatchEvent(new Event('input', { bubbles: true }));
            lastCheckbox.dispatchEvent(new Event('change', { bubbles: true }));
            
            if (this.handleFilterChange) this.handleFilterChange({ target: lastCheckbox });
            
            this.updateFilterSubtitles();
            this.updateQuickFilterButtonStates();
            this.updateFilterCountBadge();
            
            document.dispatchEvent(new CustomEvent('on:drawer-quick-filters:clicked'));
          }
        });
      });
      
      this.updateQuickFilterButtonStates();
      
      document.addEventListener('on:collection-quick-filters:clicked', () => {
        setTimeout(() => {
          quickFilterBtns.forEach((btn) => {
            const filterValues = btn.getAttribute('data-quick-filter-value').split(',').map(v => v.trim());
            const filterParam = btn.getAttribute('data-quick-filter-param') || '';
            const checkboxes = filterValues.map(value => this.findCheckboxByLabelOrParam(filterParam, value)).filter(Boolean);
            const allChecked = checkboxes.length > 0 && checkboxes.every(cb => cb.checked);
            btn.classList.toggle('is-active', allChecked);
          });
          this.updateQuickFilterButtonStates();
        }, 100);
      });
      
      document.addEventListener('on:collection-quick-filters:updated', () => {
        setTimeout(() => this.updateQuickFilterButtonStates(), 100);
      });
    }

    updateQuickFilterButtonStates() {
      const quickFilterBtns = this.querySelectorAll('[data-quick-filter-value]');
      
      quickFilterBtns.forEach((btn) => {
        if (btn.classList.contains('is-active')) {
          btn.classList.remove('is-disabled');
          return;
        }
        
        const filterValues = btn.getAttribute('data-quick-filter-value').split(',').map(v => v.trim());
        const filterParam = btn.getAttribute('data-quick-filter-param') || '';
        
        const hasAvailableOption = filterValues.some(value => this.isQuickFilterAvailable(filterParam, value));
        btn.classList.toggle('is-disabled', !hasAvailableOption);
      });
    }

    isQuickFilterAvailable(filterParam, value) {
      const checkbox = this.findCheckboxByLabelOrParam(filterParam, value);
      if (!checkbox || checkbox.disabled) return false;
      if (checkbox.checked) return true;
      
      const label = this.form.querySelector(`label[for="${checkbox.id}"]`);
      if (label) {
        const countSpan = label.querySelector('.filter__count');
        if (countSpan) {
          const count = parseInt(countSpan.textContent.replace(/[()]/g, ''));
          return count > 0;
        }
      }
      
      return !checkbox.disabled;
    }

    findCheckboxByLabelOrParam(paramName, value) {
      if (!this.form) return null;
      
      const searchValue = value.toLowerCase().trim();
      const searchParam = paramName ? paramName.toLowerCase().trim() : '';
      
      const labels = this.form.querySelectorAll('.filter__label');
      
      for (const label of labels) {
        const labelSpan = label.querySelector('.flex-auto');
        if (labelSpan) {
          const text = labelSpan.textContent.toLowerCase().trim();
          if (text === searchValue) {
            const checkboxId = label.getAttribute('for');
            if (checkboxId) {
              const checkbox = document.getElementById(checkboxId);
              if (checkbox) {
                if (searchParam) {
                  const cbName = checkbox.name.toLowerCase();
                  if (cbName.includes(searchParam) || searchParam.includes(cbName.replace('[]', ''))) {
                    return checkbox;
                  }
                } else {
                  return checkbox;
                }
              }
            }
          }
        }
      }
      
      const allCheckboxes = this.form.querySelectorAll('input[type="checkbox"]');
      
      for (const checkbox of allCheckboxes) {
        const cbValue = checkbox.value.toLowerCase().trim();
        
        if (cbValue === searchValue) {
          if (searchParam) {
            const cbName = checkbox.name.toLowerCase();
            if (cbName.includes(searchParam) || searchParam.includes(cbName.replace('[]', ''))) {
              return checkbox;
            }
          } else {
            return checkbox;
          }
        }
      }
      
      return null;
    }

    initSearchInputs() {
      const searchInputs = this.querySelectorAll('[data-target-list]');
      
      if (this._searchClearHandler) {
        this.form.removeEventListener('change', this._searchClearHandler);
        this._searchClearHandler = null;
      }
      
      searchInputs.forEach((input) => {
        const targetId = input.getAttribute('data-target-list');
        const targetList = targetId ? document.getElementById(targetId) : null;
        
        if (!targetList) return;
        
        if (input._inputHandler) {
          input.removeEventListener('input', input._inputHandler);
          input._inputHandler = null;
        }
        if (input._keydownHandler) {
          input.removeEventListener('keydown', input._keydownHandler);
          input._keydownHandler = null;
        }
        
        input._inputHandler = () => {
          const term = input.value.toLowerCase().trim();
          this.searchInputValues[targetId] = input.value;
          this.filterListBySearch(targetList, term);
        };
        
        input._keydownHandler = (e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            e.stopPropagation();
          }
        };
        
        input.addEventListener('input', input._inputHandler);
        input.addEventListener('keydown', input._keydownHandler);
        
        const clearBtn = input.parentElement?.querySelector('.filter__search-clear');
        if (clearBtn) {
          const updateClearButton = () => {
            clearBtn.style.display = input.value ? 'flex' : 'none';
          };
          
          updateClearButton();
          input.addEventListener('input', updateClearButton);
          
          if (clearBtn._clickHandler) {
            clearBtn.removeEventListener('click', clearBtn._clickHandler);
          }
          
          clearBtn._clickHandler = (e) => {
            e.preventDefault();
            e.stopPropagation();
            input.value = '';
            this.searchInputValues[targetId] = '';
            this.filterListBySearch(targetList, '');
            updateClearButton();
            input.focus();
          };
          
          clearBtn.addEventListener('click', clearBtn._clickHandler);
        }
      });
      
      this._searchClearHandler = (e) => {
        if (e.target.type === 'search' || e.target.type === 'text') return;
        
        if (e.target.type === 'checkbox' && e.target.checked) {
          const listItem = e.target.closest('li');
          if (!listItem) return;
          
          const filterList = listItem.closest('ul[id^="filter-list-"]');
          if (!filterList) return;
          
          const listId = filterList.id;
          const searchInput = this.querySelector(`[data-target-list="${listId}"]`);
          
          if (searchInput && searchInput.value) {
            searchInput.value = '';
            this.searchInputValues[listId] = '';
            this.filterListBySearch(filterList, '');
          }
        }
      };
      
      this.form.addEventListener('change', this._searchClearHandler);
    }

    filterListBySearch(list, term) {
  const items = list.querySelectorAll('li');
  
  // Normalize function to remove accents/umlauts
  const normalize = (str) => {
    return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  };
  
  const normalizedTerm = normalize(term);
  
  items.forEach((item) => {
    const val = item.getAttribute('data-search-value') || '';
    const normalizedVal = normalize(val);
    const isMatch = (normalizedTerm === '' || normalizedVal.indexOf(normalizedTerm) !== -1);
    
    if (normalizedTerm !== '') {
      item.classList.remove('js-hidden');
      item.style.display = isMatch ? '' : 'none';
    } else {
      if (item.classList.contains('filter-hidden-option')) {
        item.classList.add('js-hidden');
      }
      item.style.display = '';
    }
  });
  
  const filter = list.closest('.filter');
  if (filter) {
    const showMoreBtn = filter.querySelector('.js-show-more');
    if (showMoreBtn) {
      showMoreBtn.style.display = normalizedTerm === '' ? '' : 'none';
    }
  }
}

    initMobileClearAll() {
      const clearBtn = document.getElementById('mobile-clear-filters');
      if (!clearBtn) return;

      clearBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();

        this.form.querySelectorAll('input[type="checkbox"]:checked').forEach((cb) => cb.checked = false);
        this.form.querySelectorAll('.price-range input[type="number"]').forEach((input) => input.value = '');

        this.updateFilterSubtitles();

        const activeFiltersSection = this.querySelector('.facets__active-filters');
        if (activeFiltersSection) activeFiltersSection.setAttribute('hidden', '');

        this.searchInputValues = {};
        this.querySelectorAll('[data-mobile-filter-search]').forEach((input) => {
          input.value = '';
          const targetId = input.getAttribute('data-target-list');
          const targetList = document.getElementById(targetId);
          if (targetList) this.filterListBySearch(targetList, '');
        });

        this.querySelectorAll('[data-quick-filter-value].is-active').forEach((btn) => {
          btn.classList.remove('is-active');
        });

        this.updateQuickFilterButtonStates();
        this.updateFilterCountBadge();
        this.handleFilterChange({ type: 'change', target: { id: 'mobile-clear' } });
      });
      
      this.updateFilterCountBadge();
    }

    updateFilterCountBadge() {
      const applyBtn = document.querySelector('.facets__apply-btn');
      if (!applyBtn) return;
      
      const checkedCheckboxes = this.form.querySelectorAll('input[type="checkbox"]:checked').length;
      const priceInputs = this.form.querySelectorAll('.price-range input[type="number"]');
      let activePriceFilters = 0;
      priceInputs.forEach((input) => {
        if (input.value) activePriceFilters++;
      });
      
      const totalFilters = checkedCheckboxes + (activePriceFilters > 0 ? 1 : 0);
      
      let badge = applyBtn.querySelector('.filter-count-badge');
      if (!badge) {
        badge = document.createElement('span');
        badge.className = 'filter-count-badge';
        applyBtn.appendChild(badge);
      }
      
      if (totalFilters > 0) {
        badge.textContent = totalFilters;
        badge.classList.remove('hidden');
      } else {
        badge.classList.add('hidden');
      }
    }

    updateFilterSubtitles() {
      this.querySelectorAll('.filter.disclosure').forEach((filterEl) => {
        const checkedCount = filterEl.querySelectorAll('input[type="checkbox"]:checked').length;
        const subtitle = filterEl.querySelector('[data-filter-subtitle]');

        if (subtitle) {
          const filterLabelPlural = subtitle.getAttribute('data-filter-label-plural') || 'items';
          subtitle.textContent = checkedCount > 0 ? `${checkedCount} selected` : `All ${filterLabelPlural}`;
        }
      });
    }

    initShowMoreLess() {
      this.querySelectorAll('.js-show-more').forEach((btn) => {
        const newBtn = btn.cloneNode(true);
        btn.parentNode.replaceChild(newBtn, btn);
        
        newBtn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();

          const filter = newBtn.closest('.filter');
          const hiddenItems = filter.querySelectorAll('.filter-hidden-option');
          const isExpanded = newBtn.classList.contains('is-expanded');

          hiddenItems.forEach((item) => item.classList.toggle('js-hidden', isExpanded));

          newBtn.classList.toggle('is-expanded');
          newBtn.textContent = isExpanded ? 'Show more' : 'Show less';

          if (!isExpanded && !this.expanded.includes(filter.id)) {
            this.expanded.push(filter.id);
          } else if (isExpanded) {
            this.expanded = this.expanded.filter(id => id !== filter.id);
          }
        });
      });
    }

    initOverlay() {
      const facetsOverlay = document.querySelector('[data-facets-overlay]');
      if (!facetsOverlay) return;

      facetsOverlay.addEventListener('click', () => this.close());

      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.attributeName === 'aria-hidden') {
            const isHidden = this.getAttribute('aria-hidden') === 'true';
            facetsOverlay.classList.toggle('is-visible', !isHidden);
            
            if (this.overlay && isHidden) {
              this.overlay.classList.remove('is-visible');
            }
            
            if (window.innerWidth < 1024) {
              document.body.style.overflow = isHidden ? '' : 'hidden';
            }
            
            if (!isHidden) {
              const content = this.querySelector('.drawer__content');
              if (content) content.scrollTop = 0;
            }
          }
        });
      });
      observer.observe(this, { attributes: true });
    }

    initSwipeToClose() {
      let startY = 0, currentY = 0, isDragging = false, startTime = 0;
      const drawer = this;
      const content = this.querySelector('.drawer__content');
      const overlay = document.querySelector('[data-facets-overlay]');
      const handle = this.querySelector('.drawer__handle');
      const header = this.querySelector('.drawer__header');

      this.addEventListener('touchstart', (e) => {
        const touchTarget = e.target;
        const isHandleOrHeader = handle?.contains(touchTarget) || header?.contains(touchTarget);
        
        if (isHandleOrHeader && content && content.scrollTop <= 5) {
          startY = e.touches[0].clientY;
          startTime = Date.now();
          isDragging = true;
          drawer.style.transition = 'none';
          if (overlay) overlay.style.transition = 'none';
        }
      }, { passive: true });

      this.addEventListener('touchmove', (e) => {
        if (!isDragging) return;
        
        currentY = e.touches[0].clientY;
        const diff = currentY - startY;
        
        if (diff > 20) {
          e.preventDefault();
          drawer.style.transform = `translateY(${diff}px)`;
          
          if (overlay) {
            const opacity = Math.max(0, 1 - (diff / 300));
            overlay.style.opacity = opacity;
          }
        }
      }, { passive: false });

      this.addEventListener('touchend', () => {
        if (!isDragging) return;
        isDragging = false;
        
        const diff = currentY - startY;
        const swipeTime = Date.now() - startTime;
        const swipeSpeed = diff / swipeTime;
        
        drawer.style.transition = 'transform 0.3s ease';
        if (overlay) overlay.style.transition = 'opacity 0.3s ease';
        
        const shouldClose = diff > 200 || (diff > 120 && swipeSpeed > 0.6);
        
        if (shouldClose) {
          drawer.style.transform = 'translateY(100%)';
          if (overlay) overlay.style.opacity = '0';
          
          setTimeout(() => {
            this.close();
            drawer.style.transform = '';
            drawer.style.transition = '';
            if (overlay) {
              overlay.style.opacity = '';
              overlay.style.transition = '';
            }
          }, 300);
        } else {
          drawer.style.transform = 'translateY(0)';
          if (overlay) overlay.style.opacity = '1';
          
          setTimeout(() => {
            drawer.style.transition = '';
            if (overlay) overlay.style.transition = '';
          }, 300);
        }
        
        startY = 0;
        currentY = 0;
      });
      
      this.addEventListener('touchcancel', () => {
        if (!isDragging) return;
        isDragging = false;
        
        drawer.style.transition = 'transform 0.3s ease';
        drawer.style.transform = 'translateY(0)';
        if (overlay) {
          overlay.style.transition = 'opacity 0.3s ease';
          overlay.style.opacity = '1';
        }
        
        setTimeout(() => {
          drawer.style.transition = '';
          if (overlay) overlay.style.transition = '';
        }, 300);
        
        startY = 0;
        currentY = 0;
      });
    }

    handleBreakpointChange() {
      if (theme.mediaMatches.lg) {
        this.setAttribute('open', '');
        this.setAttribute('aria-hidden', 'false');
        this.removeAttribute('aria-modal');
        this.removeAttribute('role');
        
        if (this.overlay) this.overlay.classList.remove('is-visible');
        
        const facetsOverlay = document.querySelector('[data-facets-overlay]');
        if (facetsOverlay) facetsOverlay.classList.remove('is-visible');
        
        document.body.style.overflow = '';
        document.body.classList.remove('fixed');
        document.body.style.top = '';
        document.documentElement.style.height = '';
      } else {
        this.setAttribute('role', 'dialog');
        this.setAttribute('aria-modal', 'true');
        this.setAttribute('aria-hidden', 'true');
        this.removeAttribute('open');
        this.hidden = false;
        
        if (this.overlay) this.overlay.classList.remove('is-visible');
        
        const facetsOverlay = document.querySelector('[data-facets-overlay]');
        if (facetsOverlay) facetsOverlay.classList.remove('is-visible');
        
        document.body.style.overflow = '';
        document.body.classList.remove('fixed');
        document.body.style.top = '';
        document.documentElement.style.height = '';
      }
    }

    handleFilterChange(evt) {
      if (evt.target && evt.target.hasAttribute && (evt.target.hasAttribute('data-mobile-filter-search') || evt.target.hasAttribute('data-target-list'))) {
        return;
      }

      if (evt.type === 'change' && !(evt.target.id?.includes('price-range') || evt.target.id?.includes('sort-by') || evt.target.id === 'mobile-clear')) return;

      if (evt.target.id?.includes('price-range') && evt.constructor.name === 'InputEvent') return;

      if (window.innerWidth < 1024) this.updateFilterSubtitles();

      clearTimeout(this.filterChangeTimeout);
const isCustomSelect = evt.target.tagName === 'CUSTOM-SELECT';
const selectedSortValue = isCustomSelect ? evt.detail?.selectedValue : null;
      this.filterChangeTimeout = setTimeout(() => {
        const formData = new FormData(this.form);
        const searchParams = new URLSearchParams(formData);
        const emptyParams = [];

        if (this.sortingEnabled) {
          let currentSortBy = searchParams.get('sort_by');

         if (isCustomSelect && selectedSortValue) {
  this.mobileSortByOptions.forEach((option) => {
    option.checked = option.value === selectedSortValue;
  });
  currentSortBy = selectedSortValue;
}

          searchParams.set('sort_by', currentSortBy);
        }

        searchParams.forEach((value, key) => {
          if (!value) emptyParams.push(key);
        });

        emptyParams.forEach((key) => searchParams.delete(key));

        this.applyFilters(searchParams.toString(), evt);
      }, 500);
    }

    handleFiltersClick(evt) {
      const { target } = evt;

      if (target.matches('.js-clear-filter')) {
        evt.preventDefault();
        this.applyFilters(new URL(evt.target.href).searchParams.toString(), evt);
      }

      if (target.matches('.js-show-more') && window.innerWidth >= 1024) {
        const filter = target.closest('.filter');
        target.remove();

        filter.querySelectorAll('li').forEach((el) => el.classList.remove('js-hidden'));

        if (!this.expanded.includes(filter.id)) {
          this.expanded.push(filter.id);
        }
      }
    }

    handleActiveFiltersClick(evt) {
      if (evt.target.tagName !== 'A') return;
      evt.preventDefault();
      this.applyFilters(new URL(evt.target.href).searchParams.toString(), evt);
    }

    handleHistoryChange(evt) {
      if (evt.state !== null) {
        let searchParams = '';

        if (evt.state && evt.state.searchParams) {
          ({ searchParams } = evt.state);
        }

        this.applyFilters(searchParams, null, false);
      }
    }

    async applyFilters(searchParams, evt, updateUrl = true) {
      try {
        const activeElement = document.activeElement;
        const activeElementId = activeElement?.id;
        const isSearchInputFocused = activeElement?.hasAttribute('data-target-list');
        const focusedSearchTargetId = isSearchInputFocused ? activeElement.getAttribute('data-target-list') : null;
        const selectionStart = isSearchInputFocused ? activeElement.selectionStart : null;
        const selectionEnd = isSearchInputFocused ? activeElement.selectionEnd : null;

        const savedSearchValues = { ...this.searchInputValues };

        const customPagination = document.querySelector('custom-pagination');
        if (customPagination) customPagination.dataset.pauseInfiniteScroll = 'true';

        this.results.classList.add('is-loading');

        const closeBtn = this.querySelector('.js-close-drawer-mob');
        if (closeBtn) {
          closeBtn.ariaDisabled = 'true';
          closeBtn.classList.add('is-loading');
        }

        let fetchUrl = `${window.location.pathname}?${searchParams}`;
        if (this.form.dataset.filterSectionId) {
          fetchUrl += `&section_id=${this.form.dataset.filterSectionId}`;
        }

        if (this.applyFiltersFetchAbortController) {
          this.applyFiltersFetchAbortController.abort('Request changed');
        }
        this.applyFiltersFetchAbortController = new AbortController();

        const response = await fetch(fetchUrl, {
          method: 'GET',
          signal: this.applyFiltersFetchAbortController.signal
        });

        if (response.ok) {
          const tmpl = document.createElement('template');
          tmpl.innerHTML = await response.text();

          this.form.querySelectorAll('details-disclosure > details').forEach((existingFilter) => {
            const target = tmpl.content.getElementById(existingFilter.id);
            if (target) target.open = existingFilter.open;
          });
          
          tmpl.content.querySelectorAll('#facets details-disclosure > details').forEach((newFilter) => {
            if (this.expanded.includes(newFilter.id)) {
              newFilter.querySelectorAll('.js-hidden').forEach((listItem) => listItem.classList.remove('js-hidden'));
              const showMoreBtn = newFilter.querySelector('.filter__more');
              if (showMoreBtn) {
                showMoreBtn.classList.add('is-expanded');
                showMoreBtn.textContent = 'Show less';
              }
            }
          });

          this.form.innerHTML = tmpl.content.getElementById('facets').innerHTML;

          const mobileResultsCount = document.getElementById('mobile-results-count');
          const newResultsCount = tmpl.content.querySelector('#mobile-results-count');
          if (mobileResultsCount && newResultsCount) {
            mobileResultsCount.textContent = newResultsCount.textContent;
          }

          if (closeBtn) {
            const newCloseBtn = tmpl.content.querySelector('.js-close-drawer-mob');
            if (newCloseBtn) {
              const hasCustomButton = closeBtn.querySelector('#mobile-results-count');
              if (!hasCustomButton) {
                closeBtn.innerText = newCloseBtn.innerText;
              }
            }
          }

          const currentResultsUl = this.results.querySelector('ul');
          this.currentResultsClass = currentResultsUl ? this.results.querySelector('ul').getAttribute('class') : this.currentResultsClass;

          this.results.innerHTML = tmpl.content.getElementById('filter-results').innerHTML;

          const newResultsUl = this.results.querySelector('ul');
          if (newResultsUl && this.currentResultsClass) newResultsUl.setAttribute('class', this.currentResultsClass);

          this.addElements();
          this.addListeners();
          
          this.searchInputValues = savedSearchValues;
          this.initSearchInputs();
          
          Object.keys(savedSearchValues).forEach((listId) => {
            const searchInput = this.querySelector(`[data-target-list="${listId}"]`);
            if (searchInput && savedSearchValues[listId]) {
              searchInput.value = savedSearchValues[listId];
              const targetList = document.getElementById(listId);
              if (targetList) {
                this.filterListBySearch(targetList, savedSearchValues[listId].toLowerCase().trim());
              }
            }
          });
          
          if (window.innerWidth < 1024) this.initMobileEnhancements();

          if (customPagination && customPagination.reload) customPagination.reload();

          if (updateUrl) FacetFilters.updateURL(searchParams);

          if (closeBtn) {
            closeBtn.classList.remove('is-loading');
            closeBtn.removeAttribute('aria-disabled');
          }

          if (customPagination) customPagination.dataset.pauseInfiniteScroll = 'false';

          if (isSearchInputFocused && focusedSearchTargetId) {
            const searchInput = this.querySelector(`[data-target-list="${focusedSearchTargetId}"]`);
            if (searchInput) {
              setTimeout(() => {
                searchInput.focus();
                if (selectionStart !== null && selectionEnd !== null) {
                  searchInput.setSelectionRange(selectionStart, selectionEnd);
                }
              }, 0);
            }
          } else if (activeElementId) {
            document.getElementById(activeElementId)?.focus();
          }

         document.dispatchEvent(new CustomEvent('on:facet-filters:updated'), { bubbles: true });
          document.dispatchEvent(new CustomEvent('on:drawer-filters:updated'));
          
          // Re-initialize pick-mix buttons after filtering
          if (window.location.pathname.includes('/collections/') && document.querySelector('.pick-mix-btn')) {
            setTimeout(() => {
              document.querySelectorAll('.pick-mix-btn').forEach(btn => {
                if (window.initPickMixButton) {
                  window.initPickMixButton(btn);
                }
              });
            }, 100);
          }
          // Re-initialize Judge.me widgets after filtering
if (typeof jdgm !== 'undefined' && jdgm.customizeBadges) {
  setTimeout(() => {
    jdgm.customizeBadges();
  }, 200);
}
        }
      } catch (error) {
        console.warn(error);
      } finally {
        this.results.classList.remove('is-loading');
      }
    }

    static updateURL(searchParams) {
      window.history.pushState(
        { searchParams },
        '',
        `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`
      );
    }
  }

  customElements.define('facet-filters', FacetFilters);
}